import { useEffect, useRef, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { products } from '@/mocks/products';

const allCategories = ['ყველა', 'კერამიკა', 'იატაკი', 'ჭერი', 'ფასადი', 'სამშენებლო ქიმია'];
const allBrands = ['ყველა', 'Laminam', 'Ardex', 'Tecnopol', 'Fodbo', 'Dicson', 'Armstrong', 'Knauf'];
const allOrigins = ['ყველა', 'იტალია', 'გერმანია', 'ესპანეთი', 'დანია'];
const sortOptions = [
  { value: 'default', label: 'ნაგულისხმევი' },
  { value: 'price-asc', label: 'ფასი: დაბალი → მაღალი' },
  { value: 'price-desc', label: 'ფასი: მაღალი → დაბალი' },
  { value: 'name', label: 'სახელი A–Z' },
];

const MAX_PRICE = 400;

export default function ShopPage() {
  const [selectedCategory, setSelectedCategory] = useState('ყველა');
  const [selectedBrand, setSelectedBrand] = useState('ყველა');
  const [selectedOrigin, setSelectedOrigin] = useState('ყველა');
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, MAX_PRICE]);
  const [sortBy, setSortBy] = useState('default');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const sectionRef = useRef<HTMLDivElement>(null);

  const filteredProducts = useMemo(() => {
    let result = [...products];
    if (selectedCategory !== 'ყველა') result = result.filter((p) => p.category === selectedCategory);
    if (selectedBrand !== 'ყველა') result = result.filter((p) => p.brand === selectedBrand);
    if (selectedOrigin !== 'ყველა') result = result.filter((p) => p.origin === selectedOrigin);
    if (selectedTag) result = result.filter((p) => p.tag === selectedTag);
    result = result.filter((p) => p.price >= priceRange[0] && p.price <= priceRange[1]);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (p) => p.name.toLowerCase().includes(q) || p.brand.toLowerCase().includes(q) || p.category.toLowerCase().includes(q)
      );
    }
    switch (sortBy) {
      case 'price-asc': result.sort((a, b) => a.price - b.price); break;
      case 'price-desc': result.sort((a, b) => b.price - a.price); break;
      case 'name': result.sort((a, b) => a.name.localeCompare(b.name)); break;
    }
    return result;
  }, [selectedCategory, selectedBrand, selectedOrigin, selectedTag, priceRange, sortBy, searchQuery]);

  const activeFiltersCount =
    (selectedCategory !== 'ყველა' ? 1 : 0) +
    (selectedBrand !== 'ყველა' ? 1 : 0) +
    (selectedOrigin !== 'ყველა' ? 1 : 0) +
    (selectedTag ? 1 : 0) +
    (priceRange[0] > 0 || priceRange[1] < MAX_PRICE ? 1 : 0);

  const clearFilters = () => {
    setSelectedCategory('ყველა');
    setSelectedBrand('ყველა');
    setSelectedOrigin('ყველა');
    setSelectedTag(null);
    setPriceRange([0, MAX_PRICE]);
    setSearchQuery('');
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100');
            entry.target.classList.remove('opacity-0', 'translate-y-4');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.05 }
    );
    const els = sectionRef.current?.querySelectorAll('.reveal-card');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [filteredProducts]);

  return (
    <div className="font-sans bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[380px] bg-[#1C1C1C] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Luxury%20construction%20materials%20showroom%20premium%20tiles%20stone%20flooring%20samples%20organized%20display%20warm%20golden%20lighting%20professional%20interior%20photography&width=1920&height=700&seq=phoenix-shop-hero-v2&orientation=landscape"
            alt="მაღაზია"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1C1C1C] via-[#1C1C1C]/85 to-transparent"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-8 md:px-14 pt-32 pb-14">
          <span className="inline-block text-[10px] font-bold tracking-[0.35em] uppercase text-[#ec3145] mb-3">მაღაზია</span>
          <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-4">
            ყველა პროდუქტი
          </h1>
          <p className="text-white/50 text-sm md:text-base max-w-lg mb-7">
            {products.length}+ პრემიუმ სამშენებლო მასალა მსოფლიოს წამყვანი ბრენდებისგან.
          </p>
          {/* Search */}
          <div className="relative max-w-lg">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-white/40 text-sm"></i>
            <input
              type="text"
              placeholder="პროდუქტის, ბრენდის ან კატეგორიის ძიება..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/35 pl-11 pr-4 py-3.5 text-sm focus:outline-none focus:border-[#ec3145] transition-colors duration-300"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Main */}
      <section className="bg-[#F5F2EE] py-10 md:py-14" ref={sectionRef}>
        <div className="max-w-7xl mx-auto px-8 md:px-14">

          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className={`hidden lg:inline-flex items-center gap-2 text-xs font-bold tracking-[0.1em] uppercase px-4 py-2.5 border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  sidebarOpen
                    ? 'bg-[#1C1C1C] text-white border-[#1C1C1C]'
                    : 'bg-white text-[#1C1C1C]/60 border-[#1C1C1C]/12 hover:border-[#1C1C1C]/30'
                }`}
              >
                <i className="ri-equalizer-line"></i>
                ფილტრები
                {activeFiltersCount > 0 && (
                  <span className="w-4 h-4 rounded-full bg-[#ec3145] text-white text-[9px] font-bold flex items-center justify-center">
                    {activeFiltersCount}
                  </span>
                )}
              </button>
              <span className="text-xs text-[#1C1C1C]/40 font-medium">
                <span className="font-bold text-[#1C1C1C]">{filteredProducts.length}</span> პროდუქტი
              </span>
              {activeFiltersCount > 0 && (
                <button
                  onClick={clearFilters}
                  className="text-xs font-semibold text-[#ec3145] hover:text-[#c9283a] transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
                >
                  <i className="ri-close-circle-line"></i>
                  გასუფთავება
                </button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center border border-[#1C1C1C]/10 bg-white">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`w-9 h-9 flex items-center justify-center transition-colors cursor-pointer ${viewMode === 'grid' ? 'bg-[#1C1C1C] text-white' : 'text-[#1C1C1C]/40 hover:text-[#1C1C1C]'}`}
                >
                  <i className="ri-grid-line text-sm"></i>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`w-9 h-9 flex items-center justify-center transition-colors cursor-pointer ${viewMode === 'list' ? 'bg-[#1C1C1C] text-white' : 'text-[#1C1C1C]/40 hover:text-[#1C1C1C]'}`}
                >
                  <i className="ri-list-check text-sm"></i>
                </button>
              </div>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-xs border border-[#1C1C1C]/10 bg-white px-3 py-2.5 text-[#1C1C1C] focus:outline-none focus:border-[#ec3145] cursor-pointer"
              >
                {sortOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Layout: sidebar + products */}
          <div className="flex gap-6">

            {/* Sidebar */}
            <aside
              className={`hidden lg:flex flex-col gap-5 flex-shrink-0 transition-all duration-300 ${
                sidebarOpen ? 'w-56 opacity-100' : 'w-0 opacity-0 overflow-hidden'
              }`}
            >
              {/* Category */}
              <div className="bg-white p-5">
                <h4 className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#1C1C1C]/35 mb-4">კატეგორია</h4>
                <div className="flex flex-col gap-1">
                  {allCategories.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setSelectedCategory(cat)}
                      className={`flex items-center justify-between px-3 py-2 text-xs font-semibold transition-all duration-150 cursor-pointer text-left ${
                        selectedCategory === cat
                          ? 'bg-[#ec3145] text-white'
                          : 'text-[#1C1C1C]/55 hover:bg-[#F5F2EE] hover:text-[#1C1C1C]'
                      }`}
                    >
                      <span>{cat}</span>
                      {selectedCategory === cat && <i className="ri-check-line text-[10px]"></i>}
                    </button>
                  ))}
                </div>
              </div>

              {/* Brand */}
              <div className="bg-white p-5">
                <h4 className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#1C1C1C]/35 mb-4">ბრენდი</h4>
                <div className="flex flex-col gap-1">
                  {allBrands.map((brand) => (
                    <button
                      key={brand}
                      onClick={() => setSelectedBrand(brand)}
                      className={`flex items-center justify-between px-3 py-2 text-xs font-semibold transition-all duration-150 cursor-pointer text-left ${
                        selectedBrand === brand
                          ? 'bg-[#ec3145] text-white'
                          : 'text-[#1C1C1C]/55 hover:bg-[#F5F2EE] hover:text-[#1C1C1C]'
                      }`}
                    >
                      <span>{brand}</span>
                      {selectedBrand === brand && <i className="ri-check-line text-[10px]"></i>}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price range */}
              <div className="bg-white p-5">
                <h4 className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#1C1C1C]/35 mb-4">ფასი (₾)</h4>
                <div className="flex items-center justify-between text-xs font-bold text-[#1C1C1C] mb-3">
                  <span>₾{priceRange[0]}</span>
                  <span>₾{priceRange[1]}{priceRange[1] >= MAX_PRICE ? '+' : ''}</span>
                </div>
                <div className="flex flex-col gap-3">
                  <div>
                    <label className="text-[10px] text-[#1C1C1C]/35 mb-1 block">მინ.</label>
                    <input
                      type="range"
                      min={0}
                      max={MAX_PRICE}
                      step={10}
                      value={priceRange[0]}
                      onChange={(e) => {
                        const val = Number(e.target.value);
                        if (val <= priceRange[1]) setPriceRange([val, priceRange[1]]);
                      }}
                      className="w-full accent-[#ec3145] cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-[#1C1C1C]/35 mb-1 block">მაქს.</label>
                    <input
                      type="range"
                      min={0}
                      max={MAX_PRICE}
                      step={10}
                      value={priceRange[1]}
                      onChange={(e) => {
                        const val = Number(e.target.value);
                        if (val >= priceRange[0]) setPriceRange([priceRange[0], val]);
                      }}
                      className="w-full accent-[#ec3145] cursor-pointer"
                    />
                  </div>
                </div>
              </div>

              {/* Origin */}
              <div className="bg-white p-5">
                <h4 className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#1C1C1C]/35 mb-4">წარმოშობა</h4>
                <div className="flex flex-col gap-1">
                  {allOrigins.map((origin) => (
                    <button
                      key={origin}
                      onClick={() => setSelectedOrigin(origin)}
                      className={`flex items-center justify-between px-3 py-2 text-xs font-semibold transition-all duration-150 cursor-pointer text-left ${
                        selectedOrigin === origin
                          ? 'bg-[#ec3145] text-white'
                          : 'text-[#1C1C1C]/55 hover:bg-[#F5F2EE] hover:text-[#1C1C1C]'
                      }`}
                    >
                      <span>{origin}</span>
                      {selectedOrigin === origin && <i className="ri-check-line text-[10px]"></i>}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tag */}
              <div className="bg-white p-5">
                <h4 className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#1C1C1C]/35 mb-4">ტიპი</h4>
                <div className="flex flex-col gap-1">
                  {['ბესტსელერი', 'პრემიუმ', 'ახალი', 'პროფესიული'].map((tag) => (
                    <button
                      key={tag}
                      onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
                      className={`flex items-center justify-between px-3 py-2 text-xs font-semibold transition-all duration-150 cursor-pointer text-left ${
                        selectedTag === tag
                          ? 'bg-[#ec3145] text-white'
                          : 'text-[#1C1C1C]/55 hover:bg-[#F5F2EE] hover:text-[#1C1C1C]'
                      }`}
                    >
                      <span>{tag}</span>
                      {selectedTag === tag && <i className="ri-check-line text-[10px]"></i>}
                    </button>
                  ))}
                </div>
              </div>
            </aside>

            {/* Products */}
            <div className="flex-1 min-w-0">
              {filteredProducts.length === 0 ? (
                <div className="text-center py-24 bg-white">
                  <i className="ri-search-line text-4xl text-[#1C1C1C]/15 mb-4 block"></i>
                  <h3 className="text-lg font-bold text-[#1C1C1C] mb-2">პროდუქტები ვერ მოიძებნა</h3>
                  <p className="text-sm text-[#1C1C1C]/40 mb-6">სცადეთ სხვა ფილტრები</p>
                  <button
                    onClick={clearFilters}
                    className="inline-flex items-center gap-2 bg-[#ec3145] text-white font-bold text-xs tracking-[0.15em] uppercase px-6 py-3 hover:bg-[#c9283a] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    ფილტრების გასუფთავება
                  </button>
                </div>
              ) : viewMode === 'grid' ? (
                <div className={`grid gap-4 ${sidebarOpen ? 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'}`}>
                  {filteredProducts.map((product, index) => (
                    <Link
                      key={product.id}
                      to={`/product/${product.id}`}
                      className="reveal-card opacity-0 translate-y-4 transition-all duration-500 group bg-white overflow-hidden cursor-pointer"
                      style={{ transitionDelay: `${(index % 12) * 40}ms` }}
                    >
                      <div className="relative overflow-hidden" style={{ height: '220px' }}>
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                        {product.tag && (
                          <div className="absolute top-3 left-3 bg-[#ec3145] text-white text-[9px] font-bold tracking-[0.2em] uppercase px-2.5 py-1">
                            {product.tag}
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-500 flex items-center justify-center">
                          <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white text-[#1C1C1C] text-[10px] font-bold tracking-[0.15em] uppercase px-4 py-2">
                            დეტალურად
                          </span>
                        </div>
                      </div>
                      <div className="p-4">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[9px] font-bold tracking-[0.25em] uppercase text-[#ec3145]">{product.brand}</span>
                          <span className="text-[9px] text-[#1C1C1C]/30">{product.origin}</span>
                        </div>
                        <h3 className="text-sm font-bold text-[#1C1C1C] mb-1 group-hover:text-[#ec3145] transition-colors duration-300 line-clamp-1">
                          {product.name}
                        </h3>
                        <p className="text-[11px] text-[#1C1C1C]/40 line-clamp-2 mb-3 leading-relaxed">{product.description}</p>
                        <div className="flex items-center justify-between pt-3 border-t border-[#1C1C1C]/5">
                          <span className="text-lg font-bold text-[#1C1C1C]">₾{product.price}</span>
                          <span className="text-[9px] text-[#1C1C1C]/30">{product.category}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {filteredProducts.map((product, index) => (
                    <Link
                      key={product.id}
                      to={`/product/${product.id}`}
                      className="reveal-card opacity-0 translate-y-4 transition-all duration-500 group bg-white overflow-hidden cursor-pointer"
                      style={{ transitionDelay: `${(index % 12) * 30}ms` }}
                    >
                      <div className="flex flex-col sm:flex-row">
                        <div className="relative w-full sm:w-40 h-40 flex-shrink-0 overflow-hidden">
                          <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                          {product.tag && (
                            <div className="absolute top-2 left-2 bg-[#ec3145] text-white text-[9px] font-bold tracking-[0.15em] uppercase px-2 py-0.5">
                              {product.tag}
                            </div>
                          )}
                        </div>
                        <div className="flex-1 p-5 flex flex-col justify-between">
                          <div>
                            <div className="flex items-center gap-3 mb-1.5">
                              <span className="text-[9px] font-bold tracking-[0.25em] uppercase text-[#ec3145]">{product.brand}</span>
                              <span className="text-[9px] text-[#1C1C1C]/30">{product.category}</span>
                              <span className="text-[9px] text-[#1C1C1C]/20">{product.origin}</span>
                            </div>
                            <h3 className="text-base font-bold text-[#1C1C1C] mb-1.5 group-hover:text-[#ec3145] transition-colors duration-300">
                              {product.name}
                            </h3>
                            <p className="text-xs text-[#1C1C1C]/45 leading-relaxed mb-2 line-clamp-2">{product.description}</p>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-[#1C1C1C]/30">
                              {product.material && <span>მასალა: {product.material}</span>}
                              {product.size && <span>ზომა: {product.size}</span>}
                              {product.thickness && <span>სისქე: {product.thickness}</span>}
                            </div>
                          </div>
                          <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#1C1C1C]/5">
                            <span className="text-xl font-bold text-[#1C1C1C]">₾{product.price}</span>
                            <span className="text-xs font-bold text-[#ec3145] flex items-center gap-1 group-hover:gap-2 transition-all duration-300">
                              დეტალურად <i className="ri-arrow-right-line"></i>
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}